import { useMemo, useState } from 'react'
import { Drawer, Modal } from '@crosscode/gravity-components'
import { useChat } from './ChatProvider'
import { ChatComposer } from './ChatComposer'
import { ChatMessageList } from './ChatMessageList'
import type { ChatMessage } from './types'
import styles from './Chat.module.css'

export function ChatDrawer() {
  const { open, setOpen, connected, onlineUsers, typingUsers, deleteMessage } = useChat()
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [editing, setEditing] = useState<ChatMessage | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ChatMessage | null>(null)

  const statusLine = useMemo(() => {
    if (!connected) return 'Нет соединения'
    if (!onlineUsers.length) return 'в сети'
    const names = onlineUsers
      .slice(0, 3)
      .map((u) => u.name.split(/\s+/)[0])
      .join(', ')
    const more = onlineUsers.length > 3 ? ` +${onlineUsers.length - 3}` : ''
    return `${onlineUsers.length} онлайн · ${names}${more}`
  }, [connected, onlineUsers])

  const typingLine = useMemo(() => {
    if (!typingUsers.length) return ''
    if (typingUsers.length === 1) return `${typingUsers[0]} печатает…`
    if (typingUsers.length === 2) return `${typingUsers[0]} и ${typingUsers[1]} печатают…`
    return `${typingUsers.slice(0, 2).join(', ')} и ещё печатают…`
  }, [typingUsers])

  return (
    <>
      <Drawer
        className={styles.shell}
        title={
          <div className={styles.headerMeta}>
            <span className={styles.headerTitle}>Общий чат</span>
            <span className={styles.headerStatus}>
              {connected ? <span className={styles.onlineDot} /> : <span className={styles.offlineDot} />}
              <span className={styles.headerStatusText}>{statusLine}</span>
            </span>
          </div>
        }
        open={open}
        onClose={() => setOpen(false)}
        size={440}
        destroyOnHidden={false}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        <div className={styles.drawer}>
          <ChatMessageList
            onReply={(m) => {
              setEditing(null)
              setReplyTo(m)
            }}
            onEdit={(m) => {
              setReplyTo(null)
              setEditing(m)
            }}
            onDelete={setDeleteTarget}
          />

          <div className={styles.typing}>{typingLine || '\u00a0'}</div>

          <ChatComposer
            replyTo={replyTo}
            editing={editing}
            onCancelReply={() => setReplyTo(null)}
            onCancelEdit={() => setEditing(null)}
          />
        </div>
      </Drawer>

      <Modal
        title="Удалить сообщение?"
        open={!!deleteTarget}
        okText="Удалить"
        cancelText="Отмена"
        okButtonProps={{ danger: true }}
        onCancel={() => setDeleteTarget(null)}
        onOk={() => {
          if (deleteTarget) deleteMessage(deleteTarget.id)
          setDeleteTarget(null)
        }}
      >
        Сообщение исчезнет у всех участников чата.
      </Modal>
    </>
  )
}
